import type { TowProRequestResponse } from '@rsos/reduxDataStore/src/features/resourceRequests';
import type { ResourceRequestQueueCategory } from '../adapters/types';
import { RESOURCE_REQUEST_ACTIVE_TIMEOUT_MS } from '../queueRetention';

export const TOWPRO_REQUEST_SUBMITTED_LABEL = 'Request Submitted';

/**
 * Parse an ISO timestamp string to epoch ms, or 0 when absent or unparseable.
 */
function parseTimestampMs(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * Latest activity timestamp for a tow request, in epoch ms. The newest
 * ingested status wins, falling back to the request's creation time.
 */
export function getTowProLatestActivityMs(
  request: TowProRequestResponse,
): number {
  let latestMs = parseTimestampMs(request.createdAt);
  for (const status of request.statuses) {
    const statusMs = parseTimestampMs(status.createdAt);
    if (statusMs > latestMs) {
      latestMs = statusMs;
    }
  }
  return latestMs;
}

/**
 * Queue section for a tow request. Closed when the request was cancelled,
 * when the user closed it (queue state), or when it has gone stale with no
 * activity; active otherwise. Digital dispatch has no error signal, so tow
 * requests never land in the Error section.
 */
export function getTowProQueueCategory(
  request: TowProRequestResponse,
  nowMs: number,
): ResourceRequestQueueCategory {
  if (request.request.cancelled || request.queueState === 'closed') {
    return 'closed';
  }
  if (
    nowMs - getTowProLatestActivityMs(request) >=
    RESOURCE_REQUEST_ACTIVE_TIMEOUT_MS
  ) {
    return 'closed';
  }
  return 'active';
}

/**
 * Status chip for a tow request queue row. A cancelled request always shows
 * as canceled. Transformed TowPro statuses carry no structured fields, so
 * any ingested status renders as a generic update and a fresh request shows
 * as submitted.
 */
export function resolveTowProStatusChip(request: TowProRequestResponse): {
  display: string;
} {
  if (request.request.cancelled) {
    return { display: 'Canceled' };
  }
  return {
    display:
      request.statuses.length > 0 ? 'Updated' : TOWPRO_REQUEST_SUBMITTED_LABEL,
  };
}

/**
 * Pickup address to show for a tow request, or null when blank so callers
 * can skip the row entirely.
 */
export function formatTowProPickupAddress(
  request: TowProRequestResponse,
): string | null {
  const address = request.request.data.addressInput.address1;
  return typeof address === 'string' && address.trim().length > 0
    ? address.trim()
    : null;
}
